import { FormEvent, useEffect, useRef, useState } from 'react'
import { usePage } from '@inertiajs/react'

const emptyForm = {
  current_password: '', confirm_rotation: '', secret_key: '', webhook_secret: '',
  webhook_payload: '', webhook_signature: '',
}

const storageKey = 'stripeCredentialRotationAttemptId'
const basePath = '/super_admin/stripe_setting'

type Rotation = {
  id: number
  status: 'pending' | 'in_progress' | 'verifying' | 'completed' | 'failed'
  checked_count: number
  total_count: number
  failure_message: string | null
}

class RequestError extends Error {
  constructor(readonly status: number) { super('Rotation request failed') }
}

// Use the CSRF token shared by the Rails/Inertia page, never a cached meta-tag token.
async function rotationRequest(url: string, csrfToken: string, body?: object): Promise<Rotation> {
  const response = await fetch(url, { // allow-direct-fetch: JSON rotation protocol requires its response body; CSRF token is supplied by shared Inertia page props
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  if (!response.ok) throw new RequestError(response.status)
  const data: unknown = await response.json()
  if (!data || typeof data !== 'object' || !('rotation' in data)) throw new Error('Invalid rotation response')
  const rotation = data.rotation as Rotation
  if (!rotation || !Number.isSafeInteger(rotation.id) || rotation.id <= 0 ||
      !['pending', 'in_progress', 'verifying', 'completed', 'failed'].includes(rotation.status) ||
      !Number.isSafeInteger(rotation.checked_count) || rotation.checked_count < 0 ||
      !Number.isSafeInteger(rotation.total_count) || rotation.total_count < rotation.checked_count) {
    throw new Error('Invalid rotation response')
  }
  return rotation
}

function savedId(): number | null {
  try {
    const value = sessionStorage.getItem(storageKey)
    const id = Number(value)
    return value && Number.isSafeInteger(id) && id > 0 ? id : null
  } catch { return null }
}

export default function StripeCredentialRotation({ overlapExpiresAt }: { overlapExpiresAt: string | null }) {
  const { csrf_token } = usePage().props as unknown as { csrf_token: string }
  const [form, setForm] = useState(emptyForm)
  const [rotation, setRotation] = useState<Rotation | null>(null)
  const [busy, setBusy] = useState(false)
  const [recovering, setRecovering] = useState(() => savedId() !== null)
  const [message, setMessage] = useState('')
  const [paused, setPaused] = useState(false)
  const [startUncertain, setStartUncertain] = useState(false)
  const locked = useRef(false)
  const mounted = useRef(true)

  const requestError = (error: unknown) => {
    if (error instanceof RequestError && [401, 403, 404].includes(error.status)) {
      try { sessionStorage.removeItem(storageKey) } catch { /* storage may be unavailable */ }
      setRotation(null)
      setStartUncertain(true)
      setMessage('Access to this rotation is unavailable. Sign in again or contact support. Do not retry this request.')
    } else {
      setMessage('Could not confirm the rotation state. No further changes will be requested until you resume and check its status.')
    }
    setPaused(true)
  }

  const advance = async (initial: Rotation) => {
    let current = initial
    try {
      while (current.status !== 'completed' && current.status !== 'failed') {
        const next = await rotationRequest(`${basePath}/advance_rotation`, csrf_token, { rotation_id: current.id })
        if (!mounted.current) return
        if (next.id !== initial.id || next.checked_count < current.checked_count) throw new Error('Unexpected rotation progress')
        setRotation(next)
        if (next.status === current.status && next.checked_count === current.checked_count) {
          setPaused(true)
          setMessage('No new checks yet. Resume to confirm status before continuing.')
          return
        }
        current = next
      }
    } catch (error) {
      if (mounted.current) requestError(error)
    }
  }

  const resume = async (id: number, continueAfterStatus: boolean) => {
    if (locked.current) return
    locked.current = true
    setBusy(true)
    setMessage('')
    try {
      const current = await rotationRequest(`${basePath}/rotation_status?${new URLSearchParams({ rotation_id: String(id) })}`, csrf_token)
      if (!mounted.current) return
      if (current.id !== id) throw new Error('Unexpected rotation ID')
      setRotation(current)
      setPaused(false)
      if (continueAfterStatus) await advance(current)
    } catch (error) {
      if (mounted.current) requestError(error)
    } finally {
      locked.current = false
      if (mounted.current) {
        setBusy(false)
        setRecovering(false)
      }
    }
  }

  useEffect(() => {
    mounted.current = true
    const id = savedId()
    if (id !== null) void resume(id, false) // Read status on reload; never restart or advance without a click.
    // Only run on mount: a fresh Inertia render must not reissue an advance request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { mounted.current = false }
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (locked.current || recovering || rotation || startUncertain) return
    locked.current = true
    setBusy(true)
    setMessage('')
    const submitted = form
    setForm(emptyForm)
    try {
      const current = await rotationRequest(`${basePath}/rotate_credentials`, csrf_token, { rotation: submitted })
      try { sessionStorage.setItem(storageKey, String(current.id)) }
      catch {
        if (mounted.current) setMessage(`Rotation #${current.id} started, but this browser could not save its ID for reload recovery. Keep this page open; contact support if you lose it.`)
      }
      if (!mounted.current) return
      setRotation(current)
      setPaused(false)
      await advance(current)
    } catch (error) {
      if (!mounted.current) return
      // A lost start response may already have created an attempt. Never POST start again automatically.
      if (error instanceof RequestError && error.status === 422) {
        setMessage('Rotation was rejected. No verified credentials were activated. Check your authorization and replacement credentials before contacting support.')
      } else {
        setStartUncertain(true)
        requestError(error)
        if (!(error instanceof RequestError && [401, 403, 404].includes(error.status))) {
          setMessage('The start result is unknown. Do not submit again until an administrator has checked whether an attempt was created.')
        }
      }
    } finally {
      locked.current = false
      if (mounted.current) setBusy(false)
    }
  }

  const startNewAttempt = () => {
    if (locked.current || busy || !rotation || !['completed', 'failed'].includes(rotation.status)) return
    try { sessionStorage.removeItem(storageKey) }
    catch {
      setMessage('Could not clear saved rotation status in this browser. Please enable session storage before starting another attempt.')
      return
    }
    setRotation(null)
    setForm(emptyForm)
    setMessage('')
    setPaused(false)
    setStartUncertain(false)
    setRecovering(false)
  }

  const inputClass = 'block mt-1 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm'
  const inProgress = rotation && !['completed', 'failed'].includes(rotation.status)
  return <form onSubmit={submit} className="bg-white rounded-xl border border-stone/20 shadow-card p-6 space-y-4">
    <h2 className="text-xl font-bold text-text-primary">API-key rotation</h2>
    <p className="text-sm text-text-muted">Use this separate flow for an existing connection. It makes read-only Stripe checks before saving: same account, same Test/Live mode, existing billing records, and a signed webhook event. It does not create charges or change enrollment. The publishable key stays unchanged.</p>
    <p className="text-sm text-text-muted">First create the replacement API key in the same Stripe account without revoking the old key. Roll the signing secret on the existing webhook endpoint with a 24-hour overlap. Supply an exact, recent event body and its Stripe-Signature header signed with the new secret. Do not use a CLI forwarding secret or an event from a different endpoint.</p>
    {overlapExpiresAt && <p className="text-sm font-semibold text-text-primary">Previous webhook secret accepted until {new Date(overlapExpiresAt).toLocaleString()}. During this window, only a verified reversal to that previous signing secret is allowed.</p>}
    <div role="status" aria-live="polite" className="text-sm text-text-primary space-y-2">
      {recovering && <p>Checking saved rotation status…</p>}
      {rotation && <p>Rotation #{rotation.id}: {rotation.status === 'completed' ? 'Completed' : rotation.status === 'failed' ? 'Failed' : 'Verification in progress'} · {rotation.checked_count} of {rotation.total_count} checks completed.</p>}
      {inProgress && rotation.checked_count === 0 && <p>Pending — no changes have been made yet. Verification only proceeds while this page advances each stage.</p>}
      {rotation?.status === 'completed' && <p className="font-semibold">Credentials rotated. Previous webhook signatures remain accepted for a 24-hour overlap. Revoke the old API key only after verifying signed webhook delivery, worker processing, billing reconciliation, and completion of old in-flight API calls; the overlap ending does not by itself mean it is safe to revoke the key.</p>}
      {rotation?.status === 'failed' && <p role="alert">Verification failed. No unverified credentials were activated. Review the connection and contact support before trying again.</p>}
      {(rotation?.status === 'completed' || rotation?.status === 'failed') && <button type="button" disabled={busy} onClick={startNewAttempt} className="px-5 py-2.5 rounded-lg bg-pinnacle-red text-white font-semibold disabled:opacity-50">Start a new attempt</button>}
      {message && <p role="alert">{message}</p>}
      {inProgress && !busy && <button type="button" onClick={() => void resume(rotation.id, true)} className="px-5 py-2.5 rounded-lg bg-pinnacle-red text-white font-semibold">{paused ? 'Resume: check status first' : 'Continue verification'}</button>}
      {busy && rotation && <p>Checking rotation status and verification stages…</p>}
    </div>
    <fieldset disabled={busy || recovering || !!rotation || startUncertain} className="space-y-4 disabled:opacity-60">
      <div className="grid md:grid-cols-2 gap-4">
        {([
          ['secret_key', 'Replacement secret API key', 'password'],
          ['webhook_secret', 'Replacement webhook signing secret', 'password'],
          ['current_password', 'Your current Super Admin password', 'password'],
          ['confirm_rotation', 'Type ROTATE to authorize verification and saving', 'text'],
        ] as const).map(([name, label, type]) => <label key={name} className="block text-sm font-semibold text-text-primary">{label}
          <input required type={type} autoComplete={name === 'current_password' ? 'current-password' : 'off'} value={form[name]} onChange={e => setForm({ ...form, [name]: e.target.value })} className={inputClass} />
        </label>)}
      </div>
      <label className="block text-sm font-semibold text-text-primary">Exact signed event body (not reformatted)
        <textarea required rows={4} autoComplete="off" spellCheck={false} value={form.webhook_payload} onChange={e => setForm({ ...form, webhook_payload: e.target.value })} className={inputClass} />
      </label>
      <label className="block text-sm font-semibold text-text-primary">Stripe-Signature header (signature must be less than five minutes old)
        <input required type="password" autoComplete="off" value={form.webhook_signature} onChange={e => setForm({ ...form, webhook_signature: e.target.value })} className={inputClass} />
      </label>
      <p className="text-sm text-text-muted">After success, verify signed delivery, worker processing, billing reconciliation, and completion of old in-flight API calls before revoking the old API key. Old webhook signatures expire automatically after 24 hours; this alone does not make API key revocation safe. If verification fails, stored credentials remain unchanged. Fields clear after every submission.</p>
      <button className="px-5 py-2.5 rounded-lg bg-pinnacle-red text-white font-semibold disabled:opacity-50">{busy ? 'Verifying continuity…' : 'Verify and rotate credentials'}</button>
    </fieldset>
  </form>
}